import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/auth/AuthContext";

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [activityType, setActivityType] = useState("all");

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setToDate(today.toISOString().split("T")[0]);
    setFromDate(thirtyDaysAgo.toISOString().split("T")[0]);
  }, []);

  // Load users for admin filter
  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/users").then((res) => {
        setUsers(res.data.users || []);
      }).catch(() => {});
    }
  }, [isAdmin]);

  const loadSessions = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (selectedUserId !== "all") params.append("user_id", selectedUserId);
      if (activityType !== "all") params.append("activity_type", activityType);

      const res = await api.get(`/reports/sessions?${params.toString()}`);
      setSessions(res.data.sessions || []);
      setSelectedIds(new Set());
    } catch (e) {
      setError("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      loadSessions();
    }
  }, [fromDate, toDate, selectedUserId, activityType]);

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (selectedUserId !== "all") params.append("user_id", selectedUserId);
      if (activityType !== "all") params.append("activity_type", activityType);

      const res = await api.get(`/reports/export?${params.toString()}`, {
        responseType: "blob",
      });

      // Download the file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `POD_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sessions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sessions.map((s) => s.session_id)));
    }
  };

  const toggleSelect = (sessionId) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(sessionId)) {
      newSet.delete(sessionId);
    } else {
      newSet.add(sessionId);
    }
    setSelectedIds(newSet);
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete("/reports/sessions/bulk", {
        data: { session_ids: Array.from(selectedIds) },
      });
      setShowDeleteDialog(false);
      setSelectedIds(new Set());
      await loadSessions();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete sessions");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="reports-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 py-8 space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="reports-title">
            Reports
          </h1>
          <p className="mt-2 text-base text-slate-600" data-testid="reports-subtitle">
            Export data and manage sessions
          </p>
        </div>

        {error && (
          <div data-testid="reports-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            {error}
          </div>
        )}

        {/* Export Section */}
        <Card data-testid="export-section" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle data-testid="export-title">Export to Excel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4 mb-4">
              <div className="space-y-2">
                <Label htmlFor="from-date">From Date</Label>
                <Input
                  id="from-date"
                  data-testid="from-date-input"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="to-date">To Date</Label>
                <Input
                  id="to-date"
                  data-testid="to-date-input"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="bg-white border-slate-300"
                />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label>User</Label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger data-testid="user-filter" className="bg-white border-slate-300">
                      <SelectValue placeholder="All Users" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="all">All Users</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Activity Type</Label>
                <Select value={activityType} onValueChange={setActivityType}>
                  <SelectTrigger data-testid="activity-filter" className="bg-white border-slate-300">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="loading">Loading</SelectItem>
                    <SelectItem value="unloading">Unloading</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                <span className="font-medium">Preview:</span> {sessions.length} sessions match your filters
              </div>
              <Button
                data-testid="export-btn"
                onClick={handleExport}
                disabled={exporting || sessions.length === 0}
                className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
              >
                {exporting ? "Exporting..." : "Download Excel Report"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Manage Sessions Section */}
        <Card data-testid="manage-section" className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle data-testid="manage-title">Manage Sessions</CardTitle>
                {isAdmin ? (
                  <p className="text-sm text-slate-600 mt-1">Select sessions to delete (Admin only)</p>
                ) : (
                  <p className="text-sm text-slate-600 mt-1">View your sessions</p>
                )}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">{selectedIds.size} selected</span>
                  <Button
                    data-testid="delete-selected-btn"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={selectedIds.size === 0}
                    className="rounded-full bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  >
                    Delete Selected
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-600">Loading...</div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8 text-slate-600">No sessions found for the selected filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sessions-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      {isAdmin && (
                        <th className="py-3 px-2">
                          <input
                            type="checkbox"
                            data-testid="select-all-checkbox"
                            className="w-4 h-4 rounded border-slate-300"
                            checked={selectedIds.size === sessions.length && sessions.length > 0}
                            onChange={toggleSelectAll}
                          />
                        </th>
                      )}
                      <th className="py-3 px-2">Session ID</th>
                      <th className="py-3 px-2">User</th>
                      <th className="py-3 px-2">Date</th>
                      <th className="py-3 px-2">Type</th>
                      <th className="py-3 px-2">Videos</th>
                      <th className="py-3 px-2">Photos</th>
                      <th className="py-3 px-2">Cases</th>
                      <th className="py-3 px-2">Sacks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s, idx) => (
                      <tr
                        key={s.session_id}
                        data-testid={`session-row-${idx}`}
                        className={`border-b border-slate-100 ${selectedIds.has(s.session_id) ? "bg-emerald-50" : ""}`}
                      >
                        {isAdmin && (
                          <td className="py-3 px-2">
                            <input
                              type="checkbox"
                              data-testid={`session-checkbox-${idx}`}
                              className="w-4 h-4 rounded border-slate-300"
                              checked={selectedIds.has(s.session_id)}
                              onChange={() => toggleSelect(s.session_id)}
                            />
                          </td>
                        )}
                        <td className="py-3 px-2 font-mono text-xs">{s.session_id}</td>
                        <td className="py-3 px-2">{s.user_name}</td>
                        <td className="py-3 px-2">{s.date}</td>
                        <td className="py-3 px-2">
                          <Badge
                            variant="outline"
                            className={
                              s.activity_type === "loading"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            }
                          >
                            {s.activity_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">{s.video_count}</td>
                        <td className="py-3 px-2">{s.photo_count}</td>
                        <td className="py-3 px-2">{s.cases}</td>
                        <td className="py-3 px-2">{s.sacks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-white border-slate-200">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {selectedIds.size} session(s)?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the selected sessions from the database. 
                Files already uploaded to Google Drive will remain there.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-rose-600 text-white hover:bg-rose-700"
              >
                {deleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
